import { useState } from 'react';
import {
  Alert, Button, CircularProgress, MenuItem, Paper, Stack, TextField, Typography,
} from '@mui/material';
import PublicLegalLayout from '../../components/legal/PublicLegalLayout';
import { PLATFORM_BRAND, PLATFORM_DELETE_APPLIES_TO, PLATFORM_DELETE_HELP, PLATFORM_SUPPORT_EMAIL } from '../../constants/platformLegal';
import { env } from '../../config/env';
import PhoneNumberField from '../../components/ui/forms/PhoneNumberField';

async function apiPost(path: string, body: Record<string, unknown>) {
  const res = await fetch(`${env.apiUrl.replace(/\/$/, '')}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (data as { message?: string })?.message || 'Request failed. Please try again.';
    // Old backends that still required tenant — make it user-friendly.
    if (/tenant/i.test(msg)) {
      throw new Error('Account deletion service is updating. Please try again shortly or contact in-app support.');
    }
    throw new Error(msg);
  }
  return data;
}

export default function DeleteAccountPage() {
  const [method, setMethod] = useState<'phone' | 'email'>('phone');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [devOtp, setDevOtp] = useState('');

  const contactReady = method === 'phone' ? phone.trim().length >= 8 : email.includes('@');
  const isDev = import.meta.env.DEV;

  const requestOtp = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setDevOtp('');
    try {
      const body: Record<string, unknown> = method === 'phone'
        ? { phone: phone.trim() }
        : { email: email.trim() };
      const res = await apiPost('/public/account/delete/request', body);
      setOtpSent(true);
      setSuccess(res.message || res.data?.message || 'If an account exists, a verification code was sent.');
      if (isDev && res.data?.otp) setDevOtp(String(res.data.otp));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const body: Record<string, unknown> = {
        otp: otp.trim(),
        ...(method === 'phone' ? { phone: phone.trim() } : { email: email.trim() }),
      };
      const res = await apiPost('/public/account/delete/confirm', body);
      setSuccess(res.message || res.data?.message || 'Your account has been deleted.');
      setOtpSent(false);
      setOtp('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLegalLayout
      title="Delete Account"
      subtitle={`Close your ${PLATFORM_BRAND} account using the phone or email you signed up with.`}
    >
      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {PLATFORM_DELETE_APPLIES_TO}
          </Typography>

          <Alert severity="info" variant="outlined">
            {PLATFORM_DELETE_HELP}
          </Alert>

          <TextField
            select
            label="Verify with"
            value={method}
            onChange={(e) => { setMethod(e.target.value as 'phone' | 'email'); setOtpSent(false); setError(''); }}
            fullWidth
            size="small"
          >
            <MenuItem value="phone">Phone</MenuItem>
            <MenuItem value="email">Email</MenuItem>
          </TextField>

          {method === 'phone' ? (
            <PhoneNumberField
              label="Phone number"
              placeholder="+91xxxxxxxxxx"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
              size="small"
            />
          ) : (
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              size="small"
            />
          )}

          {otpSent && (
            <TextField
              label="Verification code"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              fullWidth
              size="small"
              helperText="Enter the code sent to your phone or email"
            />
          )}

          {isDev && devOtp && (
            <Alert severity="info">Dev only OTP: <strong>{devOtp}</strong></Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <Button
              variant={otpSent ? 'outlined' : 'contained'}
              disabled={loading || !contactReady}
              onClick={() => void requestOtp()}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {loading && !otpSent ? <CircularProgress size={18} color="inherit" /> : otpSent ? 'Resend code' : 'Send verification code'}
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={loading || !otpSent || otp.trim().length < 4}
              onClick={() => void confirmDelete()}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {loading && otpSent ? <CircularProgress size={18} color="inherit" /> : 'Delete my account'}
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Need help? Contact support at{' '}
            <a href={`mailto:${PLATFORM_SUPPORT_EMAIL}`}>{PLATFORM_SUPPORT_EMAIL}</a>
            {' '}or from inside the app after signing in with another device or account if needed.
          </Typography>
        </Stack>
      </Paper>
    </PublicLegalLayout>
  );
}
